import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import FadeIn from "../components/FadeIn";
import { CATEGORIES, SERVICES } from "../data/salon";

export default function Servicii() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    }
  }, [hash]);

  return (
    <main data-testid="services-page">
      {/* Hero */}
      <section className="pt-40 pb-20 md:pt-48 md:pb-28 marble-soft">
        <div className="mx-auto max-w-[1100px] px-6 md:px-12 text-center">
          <FadeIn>
            <p className="eyebrow mb-6">— Servicii —</p>
          </FadeIn>
          <FadeIn delay={120}>
            <h1
              data-testid="services-headline"
              className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.05]"
              style={{ color: "#1A1A1A", fontWeight: 300 }}
            >
              Patru lumi, o singură <span style={{ fontStyle: "italic", color: "#B8964E" }}>filozofie</span>.
            </h1>
          </FadeIn>
          <FadeIn delay={240}>
            <p className="mt-8 max-w-2xl mx-auto text-lg leading-relaxed" style={{ color: "#555" }}>
              De la transformări complete de păr și ten, până la detalii fine
              ale unghiilor și sprâncenelor — fiecare serviciu este executat cu
              grijă editorială.
            </p>
          </FadeIn>

          <FadeIn delay={360}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
              {CATEGORIES.map((c) => (
                <a
                  key={c.id}
                  href={`#${c.id}`}
                  data-testid={`services-anchor-${c.id}`}
                  className="px-5 py-2.5 text-xs tracking-wider-luxe border transition-all"
                  style={{ borderColor: "#E8E0D5", color: "#1A1A1A" }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#B8964E";
                    e.currentTarget.style.color = "#B8964E";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#E8E0D5";
                    e.currentTarget.style.color = "#1A1A1A";
                  }}
                >
                  {c.tag} · {c.label.toUpperCase()}
                </a>
              ))}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Category sections */}
      {CATEGORIES.map((cat, idx) => {
        const list = SERVICES[cat.id] || [];
        const flipped = idx % 2 === 1;
        return (
          <section
            id={cat.id}
            key={cat.id}
            data-testid={`services-section-${cat.id}`}
            className={`py-24 md:py-32 ${flipped ? "marble-soft" : ""}`}
            style={{ borderTop: idx === 0 ? "none" : "1px solid #E8E0D5" }}
          >
            <div className="mx-auto max-w-[1300px] px-6 md:px-12">
              <div className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start ${flipped ? "lg:[&>div:first-child]:order-2" : ""}`}>
                {/* Image + intro */}
                <div className="lg:col-span-5">
                  <FadeIn>
                    <div className="overflow-hidden" style={{ aspectRatio: "4/5" }}>
                      <img
                        src={cat.image}
                        alt={cat.label}
                        className="w-full h-full object-cover"
                        style={{ filter: "saturate(0.9)" }}
                      />
                    </div>
                  </FadeIn>
                  <FadeIn delay={120}>
                    <div className="mt-8">
                      <p
                        className="font-serif-display"
                        style={{ color: "#B8964E", fontSize: "0.9rem", letterSpacing: "0.3em" }}
                      >
                        — {cat.tag} —
                      </p>
                      <h2
                        className="font-serif-display text-4xl sm:text-5xl mt-3"
                        style={{ color: "#1A1A1A", fontWeight: 300 }}
                      >
                        {cat.label}
                      </h2>
                      <p className="mt-5 text-base leading-relaxed" style={{ color: "#555" }}>
                        {cat.description}
                      </p>
                    </div>
                  </FadeIn>
                </div>

                {/* Services list */}
                <div className="lg:col-span-7">
                  <FadeIn delay={150}>
                    <ul className="flex flex-col" style={{ borderTop: "1px solid #E8E0D5" }}>
                      {list.map((s) => (
                        <li
                          key={s.name}
                          className="grid grid-cols-12 gap-4 py-7 items-start group"
                          style={{ borderBottom: "1px solid #E8E0D5" }}
                        >
                          <div className="col-span-12 sm:col-span-7">
                            <p
                              className="font-serif-display text-2xl"
                              style={{ color: "#1A1A1A", fontWeight: 300 }}
                            >
                              {s.name}
                            </p>
                            <p className="mt-2 text-sm leading-relaxed" style={{ color: "#555" }}>
                              {s.desc}
                            </p>
                          </div>
                          <div className="col-span-6 sm:col-span-3 flex sm:justify-center">
                            <span className="eyebrow" style={{ color: "#999" }}>
                              {s.duration}
                            </span>
                          </div>
                          <div className="col-span-6 sm:col-span-2 flex sm:justify-end">
                            <span
                              className="font-serif-display text-2xl"
                              style={{ color: "#B8964E", fontWeight: 400 }}
                            >
                              {s.price}
                              <span className="text-xs ml-1" style={{ color: "#999", letterSpacing: "0.2em" }}>RON</span>
                            </span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </FadeIn>

                  <FadeIn delay={200}>
                    <div className="mt-10">
                      <Link
                        to="/rezervari"
                        data-testid={`services-book-${cat.id}`}
                        className="btn-lux"
                      >
                        Programează un serviciu {cat.label}
                        <ArrowUpRight size={14} />
                      </Link>
                    </div>
                  </FadeIn>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Closing strip */}
      <section className="py-20 md:py-28" style={{ borderTop: "1px solid #E8E0D5" }}>
        <div className="mx-auto max-w-[900px] px-6 text-center">
          <FadeIn>
            <p
              className="font-serif-display italic text-2xl sm:text-3xl leading-relaxed"
              style={{ color: "#1A1A1A", fontWeight: 300 }}
            >
              „Toate prețurile pot varia în funcție de lungimea părului sau complexitatea serviciului.”
            </p>
            <div className="divider-gold mt-10">
              <span style={{ fontSize: "0.6rem", letterSpacing: "0.3em" }}>· LUMIÈRE ·</span>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
}
